import { z } from 'zod';
import { UserTestStartMode } from '@intra-alt/shared-types';
import { apiClient } from '@/lib/http/client';
import type { ApiResponse } from '@/lib/http/types';
import { mapUserTestStatus } from '@/lib/utils/user-test-status';
import type { EvaluationStatus, TechTest } from '../types/tech-test';

// ─── Schemas Zod ──────────────────────────────────────────────────────────────

const ApiDescriptionSchema = z.object({
  name: z.string(),
  content: z.string(),
});

const ApiEvaluationSchema = z.object({
  _key: z.string(),
  name: z.string().nullable().optional(),
  duration: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  skills: z.array(z.string()).default([]),
  dependencies: z.array(z.string()).default([]),
  scheduledStartAt: z.string().nullable().optional(),
});

const ApiAttemptSchema = z.object({
  _key: z.string(),
  status: z.string(),
});

const ApiPlanningItemSchema = z.object({
  startDate: z.string().nullable().optional(),
  evaluation: ApiEvaluationSchema,
  attempts: z.array(ApiAttemptSchema).default([]),
});

const ApiUserTestSchema = z.object({
  _key: z.string(),
  userId: z.string().optional(),
  status: z.string(),
  // `.catch(undefined)` : un mode inconnu ne doit pas faire échouer tout le parsing.
  startMode: z.enum(UserTestStartMode).optional().catch(undefined),
  scheduledStartAt: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  deadline: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  preview: z.boolean().nullable().optional(),
  test: z
    .object({
      _key: z.string(),
      name: z.string(),
      descriptions: z.array(ApiDescriptionSchema).nullable().optional(),
      preview: z.boolean().nullable().optional(),
    })
    .optional(),
  // Absent pour un candidat, et pour un utilisateur supprimé : jamais requis.
  user: z
    .object({
      firstname: z.string().nullish(),
      lastname: z.string().nullish(),
      email: z.string().nullish(),
    })
    .nullish(),
  evaluationsCount: z.number().optional(),
  planning: z.array(ApiPlanningItemSchema).default([]),
});

/** Nom affiché de l'assigné : prénom + nom, à défaut l'email ; `undefined` si rien d'exploitable. */
function mapAssignee(raw: z.infer<typeof ApiUserTestSchema>): TechTest['assignee'] {
  const { user, userId } = raw;
  if (!user) return undefined;
  const fullName = [user.firstname, user.lastname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const name = fullName || user.email;
  if (!name) return undefined;
  return {
    name,
    ...(userId && { id: userId }),
    ...(user.email && user.email !== name && { email: user.email }),
  };
}

// ─── Duration parser ──────────────────────────────────────────────────────────

function parseDuration(raw: string | null | undefined): number {
  if (!raw) return 0;
  const lower = raw.toLowerCase().trim();
  const days = /^(\d+)\s*d$/i.exec(lower);
  if (days) return parseInt(days[1] ?? '0', 10) * 8;
  const hours = /^(\d+)\s*h$/i.exec(lower);
  if (hours) return parseInt(hours[1] ?? '0', 10);
  return 0;
}

// ─── Safe date parser ─────────────────────────────────────────────────────────

function safeTs(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (ddmmyyyy) {
    const ms = new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`).getTime();
    return isNaN(ms) ? undefined : ms;
  }
  const ms = new Date(value).getTime();
  return isNaN(ms) ? undefined : ms;
}

// ─── Eval status derivation ───────────────────────────────────────────────────

type RawPlanning = z.infer<typeof ApiPlanningItemSchema>;

// Statuts d'un attempt dont le travail du candidat est terminé. `abandoned`
// n'en fait PAS partie : c'est une sortie d'échec, pas une soumission. Aligné
// sur le mapper du détail (userTestApi.ts, `deriveEvalStatus`) — les deux
// surfaces doivent produire la même sémantique.
const COMPLETED_ATTEMPT_STATUSES = new Set([
  'confirmed',
  'ended',
  'analyse_in_progress',
  'analysed',
]);

function deriveEvalStatus(item: RawPlanning): EvaluationStatus {
  const attempts = item.attempts;
  if (attempts.some((a) => COMPLETED_ATTEMPT_STATUSES.has(a.status))) return 'completed';
  if (attempts.some((a) => a.status === 'in_progress')) return 'in-progress';
  // Attempt abandonné, sans reprise en cours ni soumission : état terminal
  // `failed` — croix rouge dans la liste d'évaluations du catalogue.
  if (attempts.some((a) => a.status === 'abandoned')) return 'failed';
  // ms-eval a renoncé à évaluer la soumission : état terminal distinct de
  // l'abandon candidat (SAAS-1018). Aligné sur le mapper du détail
  // (userTestApi.ts) — les deux surfaces doivent produire la même sémantique.
  if (attempts.some((a) => a.status === 'evaluation_failed')) return 'evaluation-failed';
  // No attempts on this eval — open by default; name-absence and dependency
  // locking are applied in a second pass (see mapUserTest).
  return 'available';
}

// ─── Mapper ───────────────────────────────────────────────────────────────────

const FALLBACK_STUB: TechTest = {
  id: 'stub',
  title: 'Test Technique',
  description: '',
  company: '—',
  position: '—',
  difficulty: 'mid',
  totalDuration: '—',
  createdAt: 0,
  status: 'not-started',
  evaluations: [],
};

const FALLBACK_EVAL = {
  title: 'Évaluation',
  description: '',
  instructions: '',
  durationHours: 0,
  technologies: [] as string[],
};

/**
 * Ce que l'endpoint n'expose pas, en valeurs neutres (SAAS-894).
 *
 * @remarks
 * La liste piochait auparavant dans le jeu de mocks **par position** :
 *
 * ```ts
 * return MOCK_TECH_TESTS[idx % MOCK_TECH_TESTS.length] ?? FALLBACK_STUB;
 * ```
 *
 * Le premier test affiché empruntait donc l'identité du premier mock, le
 * deuxième celle du deuxième, et ainsi de suite. Deux utilisateurs dont les
 * tests ne sont pas rangés dans le même ordre voyaient des entreprises et des
 * descriptions d'évaluation différentes **pour le même test**.
 */
const stub: TechTest = FALLBACK_STUB;

function mapUserTest(raw: z.infer<typeof ApiUserTestSchema>): TechTest {
  const description =
    raw.test?.descriptions?.map((d) => `### ${d.name}\n${d.content}`).join('\n\n') ??
    stub.description;

  const evaluations = (() => {
    // Index planning by evaluation key for dependency lookup
    const planningByKey = new Map(raw.planning.map((item) => [item.evaluation._key, item]));

    const mapped = raw.planning.map((item, evalIdx) => {
      // `description`, `instructions` et `technologies` sortaient d'une
      // évaluation du jeu de mocks, choisie par sa position dans le planning.
      // Le catalogue affichait donc des consignes et un décompte de livrables
      // sans rapport avec l'évaluation décrite (SAAS-894). Le détail, lui, les
      // tient de l'API — c'est cette forme-là qui fait foi.
      const stubEval = FALLBACK_EVAL;
      const evalStatus = deriveEvalStatus(item);
      // Name is revealed iff the back actually returned it on the evaluation object.
      const nameRevealed = item.evaluation.name != null;
      return {
        id: item.evaluation._key,
        title: nameRevealed
          ? (item.evaluation.name ?? stubEval.title)
          : `Évaluation ${evalIdx + 1}`,
        description: stubEval.description,
        instructions: stubEval.instructions,
        status: evalStatus,
        order: evalIdx + 1,
        dependencies: item.evaluation.dependencies,
        durationHours: parseDuration(item.evaluation.duration) || stubEval.durationHours,
        nameRevealed,
        resources: [],
        technologies: stubEval.technologies,
        // Real API skills evaluated on this evaluation.
        skills: item.evaluation.skills,
        // Real API tags — drive the context badge (vs the mock `technologies`).
        tags: item.evaluation.tags,
      };
    });

    // Second pass — locking rules:
    //  1. name not returned by the back → locked (name is already hidden);
    //  2. a dependency whose target evaluation has no attempt yet → locked.
    // Evaluations already completed or in progress keep their status.
    return mapped.map((ev) => {
      // `failed`/`evaluation-failed` sont terminaux au même titre que
      // `completed`/`in-progress` : ils ne doivent jamais être réécrits en
      // `locked` par les règles ci-dessous.
      if (
        ev.status === 'completed' ||
        ev.status === 'in-progress' ||
        ev.status === 'failed' ||
        ev.status === 'evaluation-failed'
      )
        return ev;
      if (!ev.nameRevealed) return { ...ev, status: 'locked' as const };
      if (ev.dependencies.length === 0) return ev;
      const depLocked = ev.dependencies.some((dep) => {
        const key = dep.split('/').pop() ?? dep;
        const depItem = planningByKey.get(key);
        if (!depItem) return true;
        return depItem.attempts.length === 0;
      });
      return depLocked ? { ...ev, status: 'locked' as const } : ev;
    });
  })();

  const totalHours = evaluations.reduce((sum, ev) => sum + ev.durationHours, 0);
  const totalDuration =
    totalHours <= 0
      ? stub.totalDuration
      : totalHours < 8
        ? `${String(totalHours)}h`
        : `${String(Math.ceil(totalHours / 8))} jour${Math.ceil(totalHours / 8) > 1 ? 's' : ''} (${String(totalHours)}h)`;

  return {
    id: raw._key,
    title: raw.test?.name ?? stub.title,
    description,
    company: stub.company,
    position: stub.position,
    difficulty: stub.difficulty,
    totalDuration,
    ...(stub.skills !== undefined && { skills: stub.skills }),
    createdAt: Date.now(),
    status: mapUserTestStatus(raw.status),
    ...(() => {
      const ts = safeTs(raw.scheduledStartAt);
      return ts !== undefined ? { scheduledStartAt: ts } : {};
    })(),
    ...(() => {
      const ts = safeTs(raw.deadline);
      return ts !== undefined ? { deadline: ts } : {};
    })(),
    ...(raw.startMode !== undefined && { startMode: raw.startMode }),
    ...(() => {
      const assignee = mapAssignee(raw);
      return assignee ? { assignee } : {};
    })(),
    evaluations,
  };
}

// ─── API call ─────────────────────────────────────────────────────────────────

export const fetchMyUserTests = async (): Promise<TechTest[]> => {
  const { data } = await apiClient.get<ApiResponse<unknown[]>>('/api/user-tests');
  return z.array(ApiUserTestSchema).parse(data.datas).map(mapUserTest);
};
